<template>
  <div v-rect-loading="loading" class="edit-status-container">
    <div class="status-editor-header">
      <div class="status-editor-title">{{ titleLabel }}</div>
    </div>
    <div class="status-info-container">
      <WrappedFrom
        ref="form"
        class="status-info"
        label-width="180px"
        :model="schoolInfo"
        :formLines="formLines"
      >
      </WrappedFrom>
      <div class="bottom-btn-group">
        <el-button class="cancel-btn" @click="handleCancel">{{ $t('common.btn.cancel') }}</el-button>
        <el-button class="save-btn" type="primary" @click="handleSave">{{ $t('common.btn.save') }}</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import WrappedFrom from '@/components/formEditor/wrappedForm.vue'
import { addI18nProperty } from '@/i18n'
import { mixedRouterPush } from '@/router/routes'

export default {
  components: {
    WrappedFrom
  },
  data () {
    return {
      i18nPrefix: 'admin.company.schoolManagement',
      loading: false,
      schoolListOptions: [],
      approvedSchoolList: [],
      schoolInfo: {
        target_school_tag_id: null,
        en_tag: null,
        tag: null,
        review_status: false
      },
      approvedSchool: {
        data: null,
        currentData: null,
        page: 1,
        pageSize: 10
      }
    }
  },
  computed: {
    tagId () {
      return this.$route.params.id
    },
    isEdit () {
      return this.$route.meta.isEdit
    },
    titleLabel () {
      if (this.isEdit) {
        return this.$t(`${this.i18nPrefix}.label.editSchool`)
      }
      return this.$t(`${this.i18nPrefix}.label.addSchool`)
    },
    formLines () {
      const vm = this
      const lines = {
        target_school_tag_id: {
          elType: 'el-select',
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.keywords`),
          label: this.$t(`${this.i18nPrefix}.label.mergeTo`),
          hidden: !this.isEdit,
          option: {
            valueKey: 'id',
            labelKey: 'i18nName'
          },
          props: {
            clearable: true,
            noDataText: this.$t(`${this.i18nPrefix}.placeholder.pleaseStartTyping`)
          },
          filterMethod: (keywords) => {
            const approvedSchool = vm.approvedSchool
            approvedSchool.page = 1
            let filteredList = []
            if (keywords) {
              const keywordsUpper = _.toUpper(keywords)
              filteredList = _.filter(vm.approvedSchoolList, (item) => _.includes(item.search, keywordsUpper))
            }
            approvedSchool.currentData = filteredList
            approvedSchool.data = filteredList.slice(0, approvedSchool.pageSize)
            return approvedSchool.data
          },
          events: {
            change: this.onSchoolSelect,
            'visible-change': (visible) => {
              if (!visible) {
                vm.approvedSchool.currentData = []
              }
            }
          },
          loadMore: (filter) => {
            const approvedSchool = vm.approvedSchool
            if (approvedSchool.currentData && approvedSchool.page * approvedSchool.pageSize > approvedSchool.currentData.length) {
              return
            }
            const data = approvedSchool.currentData.slice(approvedSchool.page * approvedSchool.pageSize, (approvedSchool.page + 1) * approvedSchool.pageSize)
            approvedSchool.page += 1
            filter.option.data.push(...data)
          }
        },
        tag: {
          label: this.$t(`${this.i18nPrefix}.label.schoolChineseName`),
          rules: [{ required: true, message: this.$t('common.message.requiredFormField'), trigger: 'blur' }]
        },
        en_tag: {
          label: this.$t(`${this.i18nPrefix}.label.schoolEnglishName`),
          rules: [{ required: true, message: this.$t('common.message.requiredFormField'), trigger: 'blur' }]
        },
        review_status: {
          elType: 'el-checkbox',
          label: this.$t(`${this.i18nPrefix}.label.reviewStatus`)
        }
      }
      return lines
    }
  },
  created () {
    this.initPage()
  },
  methods: {
    ...mapActions('schoolManagement', ['getSchoolManagementList', 'createSchool', 'updateSchool']),
    ...mapActions('application', ['getSchoolList']),
    async initPage () {
      this.loading = true
      try {
        await Promise.all([
          this.loadSchoolTagInfo(),
          this.getApprovedSchoolList()
        ])
      } finally {
        this.loading = false
      }
    },
    async getFormRef () {
      if (!this.$refs.form) {
        await this.$nextTick()
      }
      return this.$refs.form
    },
    async getApprovedSchoolList () {
      if (!this.tagId) {
        return
      }
      const result = await this.getSchoolList()
      const formatSchool = _.chain(result.schools)
        .filter(item => (item.cn_name?.trim() && item.name?.trim()))
        .map(item => {
          addI18nProperty(item, 'i18nName', 'cn_name', 'name')
          item.search = _.toUpper(`${item.cn_name}|${item.name}`)
          return item
        })
        .value()
      this.approvedSchoolList = formatSchool
    },
    async loadSchoolTagInfo () {
      if (!this.tagId) {
        return
      }

      const resp = await this.getSchoolManagementList({ tag_id: this.tagId })
      if (resp) {
        this.schoolInfo = resp
      }
    },
    handleCancel () {
      const backUrl = this.$route.query.back
      if (backUrl) {
        mixedRouterPush(this.$route.query.back)
      } else {
        this.$router.go(-1)
      }
    },
    async handleSave () {
      const form = await this.getFormRef()
      const isValid = await form.submit()
      if (!isValid) {
        return null
      }
      this.loading = true
      let resp = null
      try {
        if (this.tagId) {
          const params = { ...this.schoolInfo }
          params.tag_id = this.tagId
          resp = await this.updateSchool(params)
        } else {
          resp = await this.createSchool(this.schoolInfo)
        }
        if (resp) {
          this.$message({
            type: 'success',
            message: this.$t('common.message.saveSuccess')
          })
          this.handleCancel()
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.edit-status-container {
  .status-editor-header {
    display: flex;
    border-bottom: 1px solid #e4e7ed;
    .status-editor-title {
      padding-top: 4px;
      padding-bottom: 18px;
      line-height: 25px;
      font-size: 18px;
      font-weight: 500;
      color: #303133;
    }
  }
  .status-info-container {
    width: 600px;
    margin: 30px auto 0;
    .status-info {}
    .bottom-btn-group {
      display: flex;
      justify-content: flex-end;

      .el-button {
        padding: 8px 28px;
        border-radius: 2px;
        line-height: 22px;
        font-size: 16px;
        font-weight: 500;
        color: #0096cc;

        &:last-child {
          margin-left: 24px;
          margin-right: 0;
          background: #0096cc;
          border-color: #0096cc;
          color: #ffffff;
        }
      }
    }
  }
}
</style>
